import { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  IconButton,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
} from '@material-ui/icons';

import PINPEO, { STATUS_PILL } from 'theme/palette';

const useStyles = makeStyles((theme) => ({
  root: {},
  backdrop: {
    backgroundColor: 'rgba(11,61,38,.35)',
  },
  paper: {
    borderRadius: 16,
    boxShadow: '0 24px 60px rgba(11,61,38,.3)',
  },
  dialogTitle: {
    '& .MuiTypography-root': {
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${PINPEO.divider}`,
  },
  button: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 13,
    borderRadius: PINPEO.radius,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  dangerButton: {
    color: theme.palette.common.white,
    backgroundColor: STATUS_PILL.error.fg,
    '&:hover': {
      backgroundColor: '#8F2C24',
    },
  },
  firstButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: PINPEO.textMuted,
  },
  secondButton: {
    position: 'absolute',
    right: theme.spacing(6),
    top: theme.spacing(1),
    color: PINPEO.textMuted,
  },
}));

function Modal(props) {
  const {
    actions,
    canFullScreen,
    children,
    events,
    fullWidth,
    maxWidth,
    open,
    title,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [forceFull, setForceFull] = useState(false);

  const { onClose } = events;

  return (
    <Dialog
      disableBackdropClick
      fullWidth={fullWidth}
      fullScreen={fullScreen || forceFull}
      maxWidth={maxWidth}
      onClose={onClose}
      open={open}
      aria-labelledby="dialog-title"
      BackdropProps={{
        classes: {
          root: classes.backdrop,
        },
      }}
      PaperProps={{
        className: classes.paper,
      }}
    >
      {title && (
        <DialogTitle
          children={title}
          className={classes.dialogTitle}
          id="dialog-title"
        />
      )}

      {(canFullScreen && !fullScreen) && (<IconButton
        aria-label="fullscreen"
        className={onClose ? classes.secondButton : classes.firstButton}
        onClick={() => setForceFull(!forceFull)}
      >
        <FullscreenIcon />
      </IconButton>)}

      {onClose &&
        <IconButton
          aria-label="close"
          className={classes.firstButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      }

      {children && (
        <DialogContent dividers>
          {children}
        </DialogContent>
      )}

      {!!actions.length && <>
        <DialogActions className={classes.dialogActions}>
          {actions.map((item, index) => (
            <Button
              children={item.label}
              className={clsx(
                classes.button,
                item.color === 'error' && !item.processing
                  ? classes.dangerButton
                  : undefined,
              )}
              color={item.color === 'error'
                ? 'default'
                : (item.processing ? 'secondary' : item.color)}
              disabled={item.processing || item.disabled}
              form={item.form}
              key={index}
              startIcon={item.processing ?
                (<CircularProgress
                  color="inherit"
                  size={18}
                  style={{
                    marginRight: 5,
                  }}
                />)
                : item.icon}
              style={{
                ...(item.hidden ? {
                  display: 'none',
                } : {}),
                ...(item.left ? {
                  marginRight: 'auto',
                } : {}),
              }}
              type={item.type}
              variant={item.color ? 'contained' : 'text'}
              onClick={item.fn || item.action}
            />
          ))}
        </DialogActions>
      </>}

    </Dialog >
  );
}

Modal.defaultProps = {
  actions: {},
  events: {},
  fullWidth: true,
  maxWidth: 'sm',
  open: false,
};

Modal.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.oneOf([
      'error',
      'primary',
      'secondary',
    ]),
    disabled: PropTypes.bool,
    fn: PropTypes.func,
    icon: PropTypes.element,
    label: PropTypes.string,
    processing: PropTypes.bool,
  })),
  canFullScreen: PropTypes.bool,
  children: PropTypes.element,
  events: PropTypes.shape({
    onClose: PropTypes.func,
  }),
  fullWidth: PropTypes.bool,
  maxWidth: PropTypes.oneOf([
    'lg',
    'md',
    'sm',
    'xl',
    'xs',
  ]),
  open: PropTypes.bool,
  title: PropTypes.string,
};

export default Modal;
